import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Typography primitives. Every heading and block of text in the app renders
 * through <Heading> or <Text> so size, weight, family, leading and tracking
 * stay consistent. Do not hand-roll `text-3xl font-black tracking-tight` in
 * feature code — pick a variant, or extend the variants here.
 *
 * Families: `font-display` (Orbitron) is display-only — never body copy, it is
 * unreadable below ~18px. `font-body` (Alexandria) is the default. `font-meta`
 * (IBM Plex Mono) is for codes, IDs, coordinates and machine metadata.
 */

export const headingVariants = cva("text-balance text-foreground", {
  variants: {
    variant: {
      /** Dashboard hero / marketing splash. */
      display: "font-display text-4xl font-black tracking-tight md:text-5xl",
      /** Route title (one per screen). */
      page: "font-display text-3xl font-black tracking-tight",
      /** Major section within a screen. */
      section: "font-display text-xl font-bold tracking-tight",
      /** Card title / sub-block heading. Body font — display is too loud here. */
      subsection: "text-base font-semibold tracking-tight",
    },
  },
  defaultVariants: { variant: "page" },
});

const headingTag = {
  display: "h1",
  page: "h1",
  section: "h2",
  subsection: "h3",
} as const;

type HeadingProps = React.ComponentProps<"h1"> &
  VariantProps<typeof headingVariants> & {
    as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "div";
  };

function Heading({ className, variant, as, ...props }: HeadingProps) {
  const Tag = as ?? headingTag[variant ?? "page"];
  return (
    <Tag
      data-slot="heading"
      className={cn(headingVariants({ variant }), className)}
      {...props}
    />
  );
}

export const textVariants = cva("", {
  variants: {
    variant: {
      /** Intro paragraph under a heading. */
      lead: "text-base leading-7",
      /** Default paragraph and content. */
      body: "text-sm leading-6",
      /** Dense secondary content. */
      small: "text-xs leading-5",
      /** Metadata, helper text, timestamps. */
      caption: "text-xs text-muted-foreground",
      /** Machine metadata: codes, IDs, coordinates. */
      meta: "font-meta text-sm text-muted-foreground",
      /** Kicker above a heading. */
      eyebrow:
        "text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground",
      /** Label-styled text (use the Label component for real <label>s). */
      label: "text-sm font-medium",
    },
    tone: {
      default: "text-foreground",
      muted: "text-muted-foreground",
      primary: "text-primary",
      destructive: "text-destructive",
    },
  },
  defaultVariants: { variant: "body" },
});

type TextProps = React.ComponentProps<"p"> &
  VariantProps<typeof textVariants> & {
    as?: "p" | "span" | "div";
  };

function Text({ className, variant, tone, as = "p", ...props }: TextProps) {
  const Tag = as;
  return (
    <Tag
      data-slot="text"
      className={cn(textVariants({ variant, tone }), className)}
      {...props}
    />
  );
}

export { Heading, Text };
